import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { BASE_URL } from "../../global"; // adjust path same as your srf helper file
import axiosWithToken from "../../utils/components/axiosTokenConfig"; // adjust path
import { parseRange } from "../../utils/components/accuracyandlcandrangesshow";

const steps = ["Basic Details", "Select DUC Instruments"];

const RenewInwardModal = ({ open, onClose, row, onConfirm }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [entryDate, setEntryDate] = useState(new Date());
  const [receiptDate, setReceiptDate] = useState(null);
  const [committedDate, setCommittedDate] = useState(null);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(false);

  const resetAndClose = () => {
    setActiveStep(0);
    setEntryDate(new Date());
    setReceiptDate(null);
    setCommittedDate(null);
    setInstruments([]);
    onClose();
  };

  // fetch instruments of the selected SRF when modal opens
  useEffect(() => {
    if (!open || !row?.id) return;
    setLoading(true);
    axiosWithToken
      .post(`${BASE_URL}dynamic`, {
        query: `SELECT si.id, si.instrumentId, i.instrumentName, si.requestedDucName, si.DUCID, si.serialNumber, si.make, si.model, si.ranges, si.lc, si.accuracy
          FROM srfInstruments AS si
          LEFT JOIN instruments AS i ON i.id = si.instrumentId AND i.status = 1
          WHERE si.srfsId = ${row.id} AND si.status = 1
          ORDER BY si.id`,
      })
      .then((res) => setInstruments(res.data || []))
      .catch((err) => console.log("renew instruments fetch error", err))
      .finally(() => setLoading(false));
  }, [open, row?.id]);

  const handleDeleteRow = (id) => {
    setInstruments((prev) => prev.filter((i) => i.id !== id));
  };

  const handleSubmit = () => {
    if (!entryDate || instruments.length === 0) return;
    const confirmed = window.confirm(
      `New Inward will be created with ${instruments.length} instrument(s).\nAre you sure you want to continue?`
    );
    if (!confirmed) return;
    onConfirm(
      entryDate,
      instruments.map((i) => i.id),
      { receiptDate, committedDeliveryDate: committedDate }
    );
    resetAndClose();
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, maxHeight: 50 }}>
        <AutorenewIcon color="primary" />
        Renew Inward
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 380 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* STEP 1: BASIC DATA */}
        {activeStep === 0 && (
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Client"
                value={row?.customerName || ""}
                size="small"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Service Request Number"
                value={row?.serviceReqNumber || ""}
                size="small"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Calibration Type"
                value={row?.calType || ""}
                size="small"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Entry Date *"
                  value={entryDate}
                  maxDate={new Date()}
                  format="dd/MM/yyyy"
                  onChange={(v) => setEntryDate(v)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Receipt Date (optional)"
                  value={receiptDate}
                  maxDate={new Date()}
                  format="dd/MM/yyyy"
                  onChange={(v) => setReceiptDate(v)}
                  slotProps={{
                    field: { clearable: true },
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Committed Delivery Date (optional)"
                  value={committedDate}
                  minDate={entryDate || undefined}
                  format="dd/MM/yyyy"
                  onChange={(v) => setCommittedDate(v)}
                  slotProps={{
                    field: { clearable: true },
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        )}

        {/* STEP 2: DUC INWARD TABLE (delete only) */}
        {activeStep === 1 && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                DUC Inward Details
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Delete the rows you don't want in the new inward. Only the
              remaining rows will be saved.
            </Typography>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 300, mt: 1, border: "1px solid #ddd" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Sr.No.</b></TableCell>
                      <TableCell><b>Instrument Name</b></TableCell>
                      <TableCell><b>RequestedDuc Name</b></TableCell>
                      <TableCell><b>DUCID</b></TableCell>
                      <TableCell><b>Serial No.</b></TableCell>
                      <TableCell><b>Range</b></TableCell>
                      <TableCell><b>LC</b></TableCell>
                      <TableCell><b>Make</b></TableCell>
                      <TableCell><b>Model</b></TableCell>
                      <TableCell align="center"><b>Delete</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {instruments.length > 0 ? (
                      instruments.map((ins, idx) => (
                        <TableRow hover key={ins.id}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{ins.instrumentName}</TableCell>
                          <TableCell>{ins.requestedDucName || "-"}</TableCell>
                          <TableCell>{ins.DUCID}</TableCell>
                          <TableCell>{parseRange(ins.ranges)}</TableCell>
                          <TableCell>{parseRange(ins.lc)}</TableCell>
                          <TableCell>{ins.serialNumber}</TableCell>
                          <TableCell>{ins.make}</TableCell>
                          <TableCell>{ins.model}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Remove from renewal">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={instruments.length === 1}
                                  onClick={() => handleDeleteRow(ins.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No instruments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={resetAndClose} color="secondary">
          Cancel
        </Button>
        {activeStep === 1 && (
          <Button onClick={() => setActiveStep(0)}>Back</Button>
        )}
        {activeStep === 0 ? (
          <Button
            variant="contained"
            disabled={!entryDate}
            onClick={() => setActiveStep(1)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={loading || instruments.length === 0}
            onClick={handleSubmit}
          >
            Update
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RenewInwardModal;